/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';
import clsx from 'clsx';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { DataGridPro } from '@mui/x-data-grid-pro';
import FormHelperText from '@mui/material/FormHelperText';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Push from '../../../components/push/';
import Validator, { useNoisyValidate, checkValidity } from '../../../components/validator';
import ConfirmationDialog from '../../../components/confirmationdlg';
import SandboxURL from '../../../components/policies/sandboxurl';

import PolicyService from '../../../data/policy-service';
import SystemService from '../../../data/system-service';
import Auth from '../../../data/auth-service';

import Helpers from '../../../libraries/helpers';
import Animations from '../../../libraries/animations';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

const useStyles = makeStyles(theme => ({
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    buttonGroup: {
        '& button': {
            float: 'right',
            marginLeft: theme.spacing(1),
        },
    },
    textFieldList: {
        '&.MuiTextField-root': {
            marginTop: theme.spacing(2),
        }
    },
    links: {
        '& .MuiLink-root': {
            marginRight: theme.spacing(2),
        },
    },
    userButtons: {
        float: 'right',
        marginTop: theme.spacing(4),
        marginRight: theme.spacing(1),
        marginLeft: theme.spacing(1),
    },
    clearfix: {
        overflow: 'auto',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    newUserForm: {
        width: '100%',
        marginLeft: 0,
        marginRight: 0,
        '& .MuiFormControlLabel-label': {
            width: '100%',
        },
        '& .MuiInputBase-root': {
            width: '100%',
        }
    },
    ...Animations.Push(theme),
    warningBox: {
        border: `1px solid ${theme.palette.warning.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
}));

const emptySandbox = { type: 'cuckoo', url: '', apikey: '', url_timeout: 11, res_wait_timeout: 500 };

const SystemConfig = () => {
    const configPerms = Auth.permissions.get('ConfigPolicy');
    const [readonly, setReadonly] = useState(!configPerms.write);
    const classes = useStyles();
    const params = useParams();
    const location = useLocation();
    const [config, setConfig] = useState({
        name: '',
        type: '',
        count: 0,
        data: {
            retention: {
                detected_malware_disk_usage: 0,
                detected_malware_max_age: 1,
                detected_malware_max_entries: 0,
                full_pcap_disk_usage: 0,
                full_pcap_max_age: 0,
                smart_pcap_disk_usage: 0,
                smart_pcap_max_age: 0,
                smart_pcap_max_entries: 0,
            },
            snmp: {
                syslocation: '',
                syscontact: '',
                communities: [],
                users: [],
            },
            sandbox: {
                configs: [],
            },
            dpid: {
                enabled: false,
            },
        },
    });
    const [newUser, setNewUser] = useState(false);
    const [newUserData, setNewUserData] = useState({
        username: '',
        seclevel: 'authPriv',
        authtype: 'SHA',
        authpass: '',
        privtype: 'AES',
        privpass: '',
    });
    const [mode, setMode] = useState('config');
    const [editUser, setEditUser] = useState('');
    const [deleteUserDialog, setDeleteUserDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [original, setOriginal] = useState({});
    const [changes, setChanges] = useState(false);
    const [snmpTable, setSNMPTable] = useState(false);
    const [pushing, setPushing] = useState(false);

    const configForm = useNoisyValidate({});

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    useEffect(() => {
        getConfig();
    }, []);

    useEffect(() => {
        setChanges(!_isEqual(config, original));
        setSNMPTable(true);
    }, [config]);

    const getConfig = () => {
        PolicyService.getPolicy('system_config', params.policy).then(res => {
            if (!res.data.snmp.users) res.data.snmp.users = [];
            if (res.data.snmp.users.length) {
                res.data.snmp.users = res.data.snmp.users.map(v => ({ ...v, id: Helpers.UniqueID() }));
            }
            if (!res.data.sandbox.configs) res.data.sandbox.configs = [];
            if (!res.data.snmp.communities) res.data.snmp.communities = [];
            if (!res.data.dpid) res.data.dpid = { enabled: false };
            setOriginal(_cloneDeep(res));
            setConfig(res);
            setReadonly(readonly || (configPerms.override ? false : (!MC2CHECK && (res.source === 'mc2' || location.pathname.includes('.mc2')))));
        }, err => {
            if (typeof err === 'string') {
                toast.error(err);
                console.error(err);
            }
        });
    };

    const handleAddSandboxURL = () => {
        const c = { ...config };
        c.data.sandbox.configs.push({ ...emptySandbox });
        setConfig(c);
    };

    const handleVerifySandboxURL = (v) => {
        SystemService.sandboxURLVerify(v).then(() => {
            toast.success('Sandbox URL validated successfully');
        }, err => {
            toast.error(err.message);
        });
    };

    const handleUpdateSandbox = (e, i) => {
        const c = { ...config };
        c.data.sandbox.configs[i] = e;
        setConfig(c);
    };

    const handleDeleteSandboxURL = (i) => {
        const c = { ...config };
        c.data.sandbox.configs.splice(i, 1);
        setConfig(c);
    };

    const handleEnableDPID = (e) => {
        const c = { ...config };
        c.data.dpid.enabled = e.target.checked;
        setConfig(current => ({
            ...current,
            data: {
                ...current.data,
                dpid: { enabled: e.target.checked },
            },
        }));
    };

    const handlePushPolicy = () => {
        setMode('push');
    };

    const handleDeployPolicy = (sensorList) => {
        setPushing(true);
        PolicyService.updatePolicy('system_config', params.policy, config).then(res => {
            toast.success('Policy saved successfully');
            setMode('config');
            getConfig();

            if (sensorList.length || Object.keys(sensorList).length) {
                PolicyService.assignPolicy('system_config', params.policy, sensorList).then(res => {
                    toast.success('Policy pushed to sensor(s) successfully');
                }, err => {
                    if (typeof err === 'string') {
                        toast.error('Pushing policy to new sensor(s) failed : ' + err);
                    } else {
                        toast.error(err.message + ', ' + err.error);
                    }
                });
            }
        }, err => {
            if (err) {
                toast.error(err.message + ': ' + err.error);
            }
        }).finally(() => { setPushing(false) });
    };

    const handleRevertPolicy = () => {
        getConfig();
    };

    const handleUpdateRetention = (e, validID, valid) => {
        configForm.set({ ...configForm.get(), [validID]: valid });

        const c = { ...config };
        c.data.retention[e.target.name] = parseInt(e.target.value) || 0;
        setConfig(c);
    };

    const handleLocation = (e) => {
        const c = { ...config };
        config.data.snmp.syslocation = e.currentTarget.value;
        setConfig(c);
    };

    const handleContact = (e) => {
        const c = { ...config };
        config.data.snmp.syscontact = e.currentTarget.value;
        setConfig(c);
    };

    const handleCommunityStrings = (e) => {
        const c = { ...config };
        config.data.snmp.communities = e.currentTarget.value ? e.currentTarget.value.replace(/\s/g, '').split(',') : [];
        setConfig(c);
    };

    const handleSelectUser = (s) => {
        setSelectedUser(s.row);
    };

    const handleEditUser = (e) => {
        setNewUserData(selectedUser);
        setNewUser(true);
        setEditUser(selectedUser.username);
    };

    const handleOpenDeleteUser = (e) => {
        setDeleteUserDialog(true);
    };

    const handleCloseDeleteUser = () => {
        setDeleteUserDialog(false);
    };

    const handleDeleteUser = () => {
        setSNMPTable(false);
        const c = { ...config };
        const i = c.data.snmp.users.findIndex((v, i) => { return v.username === selectedUser.username });
        c.data.snmp.users.splice(i, 1);
        setConfig(c);
        setDeleteUserDialog(false);
        setSelectedUser(null);
    };

    const handleUsername = (e, validID, valid) => {
        configForm.set({ ...configForm.get(), [validID]: valid });

        setNewUserData({
            ...newUserData,
            username: e.target.value,
        });
    };

    const handleSeclevel = (e) => {
        setNewUserData({
            ...newUserData,
            seclevel: e.target.value,
        });
    };

    const handleAuthType = (e) => {
        setNewUserData({
            ...newUserData,
            authtype: e.target.value,
        });
    };

    const handleAuthPass = (e, validID, valid) => {
        configForm.set({
            ...configForm.get(),
            [validID]: editUser && e.target.value === '***' ? true : valid,
        });

        setNewUserData({
            ...newUserData,
            authpass: e.target.value,
        });
    };

    const handlePrivType = (e) => {
        setNewUserData({
            ...newUserData,
            privtype: e.target.value,
        });
    };

    const handlePrivPass = (e, validID, valid) => {
        configForm.set({
            ...configForm.get(),
            [validID]: editUser && e.target.value === '***' ? true : valid,
        });

        setNewUserData({
            ...newUserData,
            privpass: e.target.value,
        });
    };

    const handleAddNewUser = () => {
        setSNMPTable(false);
        const c = _cloneDeep(config);
        if (editUser) {
            const i = c.data.snmp.users.findIndex((v, i) => { return v.username === editUser });
            c.data.snmp.users[i] = {
                ...newUserData,
                id: Helpers.UniqueID(),
            };
        } else {
            c.data.snmp.users.push({
                ...newUserData,
                id: Helpers.UniqueID(),
            });
        }
        setConfig(c);
        handleCancelNewUser();
    };

    const handleCancelNewUser = () => {
        setEditUser('');
        setNewUserData({ username: '', seclevel: 'authPriv', authtype: 'SHA', authpass: '', privtype: 'AES', privpass: '' });
        setNewUser(false);
    };

    const handleRemoveValidation = (validID) => {
        const c = configForm.get();
        delete c[validID];
        configForm.set(c);
    };

    const handleValidation = (validID, valid) => {
        configForm.set({ ...configForm.get(), [validID]: valid });
    };

    const handleDownloadMIB = (file) => {
        SystemService.downloadMIB(file + '.txt').then(res => {
            const a = document.createElement('a');
            a.target = '_blank';
            a.download = `${file}.txt`;
            a.href = URL.createObjectURL(new Blob([res], { type: 'application/octet-stream' }), `${file}.txt`);
            a.click();
        });
    };

    return (
        <Grid container spacing={2} style={{ marginBottom: '30px' }}>
            <Grid container item xs={12} className={classes.justifySpace}>
                <Grid container item xs={12} style={{ display: mode === 'push' ? 'none' : 'flex' }}>
                    <Grid item xs={12} className={classes.buttonGroup}>
                        {!MC2CHECK && config.source === 'mc2' && <Box mb={1} className={classes.warningBox} display="inline-block">
                            You cannot modify or push a policy assigned by an MC2.
                        </Box>}
                        {!readonly && <Button
                            variant="contained"
                            color="secondary"
                            aria-label="revert"
                            onClick={handleRevertPolicy}
                            disabled={!MC2CHECK && config.source === 'mc2'}
                        >Revert</Button>}
                        {configPerms.admin && <Button
                            variant="contained"
                            color="primary"
                            aria-label="push"
                            onClick={handlePushPolicy}
                            disabled={!checkValidity(configForm.get()) || (!MC2CHECK && config.source === 'mc2')}
                            className={clsx({
                                [classes.pushAnimation]: !(!MC2CHECK && config.source === 'mc2') && changes,
                            })}
                        >Push</Button>}
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} style={{ display: mode === 'push' ? 'block' : 'none' }}>
                {configPerms.admin && <Push
                    policyName={params.policy}
                    policyType={'system_config'}
                    onDeploy={handleDeployPolicy}
                    closePush={() => { setMode('config') }}
                    pushing={pushing}
                    permName={'ConfigPolicy'}
                />}
            </Grid>
            {/*data retention*/}
            <Grid item xs={12} style={{ display: mode === 'config' ? 'block' : 'none' }}>
                <Paper elevation={3}>
                    <Box p={2}>
                        <Typography variant="h6">Data Retention</Typography>
                        <Box mt={2}>
                            <Grid container item xs={12} spacing={2}>
                                <Grid item xs={6}>
                                    <Box mb={2}><Validator
                                        valueType="range"
                                        errorText="Total disk usage between Full PCAP, Smart PCAP, and Detected Malware cannot exceed 90"
                                        inputProps={{
                                            min: 0,
                                            max: 90 -
                                                (config.data.retention.smart_pcap_disk_usage +
                                                config.data.retention.detected_malware_disk_usage),
                                        }}
                                        kick={ 90 -
                                            (config.data.retention.smart_pcap_disk_usage +
                                            config.data.retention.detected_malware_disk_usage)}
                                        fullWidth
                                        variant="standard"
                                        label="Full PCAP Disk Usage"
                                        name="full_pcap_disk_usage"
                                        value={config.data.retention.full_pcap_disk_usage}
                                        onChange={handleUpdateRetention}
                                        endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                        disabled={readonly}
                                    /></Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box mb={2}><Validator
                                        valueType="range"
                                        errorText=""
                                        inputProps={{
                                            min: 1,
                                            max: 3650,
                                        }}
                                        fullWidth
                                        variant="standard"
                                        label="Full PCAP Max Age"
                                        name="full_pcap_max_age"
                                        value={config.data.retention.full_pcap_max_age}
                                        onChange={handleUpdateRetention}
                                        endAdornment={<InputAdornment position="end">days</InputAdornment>}
                                        disabled={readonly}
                                    /></Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box mb={2}><Validator
                                        valueType="range"
                                        errorText="Total disk usage between Full PCAP, Smart PCAP, and Detected Malware cannot exceed 90"
                                        inputProps={{
                                            min: 0,
                                            max: 90 -
                                                (config.data.retention.full_pcap_disk_usage +
                                                config.data.retention.detected_malware_disk_usage),
                                        }}
                                        kick={90 -
                                            (config.data.retention.full_pcap_disk_usage +
                                            config.data.retention.detected_malware_disk_usage)}
                                        fullWidth
                                        variant="standard"
                                        label="Smart PCAP Disk Usage"
                                        name="smart_pcap_disk_usage"
                                        value={config.data.retention.smart_pcap_disk_usage}
                                        onChange={handleUpdateRetention}
                                        endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                        disabled={readonly}
                                    /></Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box mb={2}><Validator
                                        valueType="range"
                                        errorText=""
                                        inputProps={{
                                            min: 0,
                                            max: 4111111,
                                        }}
                                        fullWidth
                                        variant="standard"
                                        label="Smart PCAP Max Entries"
                                        name="smart_pcap_max_entries"
                                        value={config.data.retention.smart_pcap_max_entries}
                                        onChange={handleUpdateRetention}
                                        endAdornment={<InputAdornment position="end">files</InputAdornment>}
                                        disabled={readonly}
                                    /></Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box mb={2}><Validator
                                        valueType="range"
                                        errorText="Total disk usage between Full PCAP, Smart PCAP, and Detected Malware cannot exceed 90"
                                        inputProps={{
                                            min: 0,
                                            max: 90 -
                                                (config.data.retention.smart_pcap_disk_usage +
                                                config.data.retention.full_pcap_disk_usage),
                                        }}
                                        kick={90 -
                                            (config.data.retention.smart_pcap_disk_usage +
                                            config.data.retention.full_pcap_disk_usage)}
                                        fullWidth
                                        variant="standard"
                                        label="Detected Malware Disk Usage"
                                        name="detected_malware_disk_usage"
                                        value={config.data.retention.detected_malware_disk_usage}
                                        onChange={handleUpdateRetention}
                                        endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                        disabled={readonly}
                                    /></Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box mb={2}><Validator
                                        valueType="range"
                                        errorText=""
                                        inputProps={{
                                            min: 0,
                                            max: 1111111,
                                        }}
                                        fullWidth
                                        variant="standard"
                                        label="Detected Malware Max Entries"
                                        name="detected_malware_max_entries"
                                        value={config.data.retention.detected_malware_max_entries}
                                        onChange={handleUpdateRetention}
                                        endAdornment={<InputAdornment position="end">files</InputAdornment>}
                                        disabled={readonly}
                                    /></Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box mb={2}><Validator
                                        valueType="range"
                                        errorText=""
                                        inputProps={{
                                            min: 0,
                                            max: 3650,
                                        }}
                                        fullWidth
                                        variant="standard"
                                        label="Smart PCAP Max Age"
                                        name="smart_pcap_max_age"
                                        value={config.data.retention.smart_pcap_max_age}
                                        onChange={handleUpdateRetention}
                                        endAdornment={<InputAdornment position="end">days</InputAdornment>}
                                        disabled={readonly}
                                    /></Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box mb={2}><Validator
                                        valueType="range"
                                        errorText=""
                                        inputProps={{
                                            min: 1,
                                            max: 3650,
                                        }}
                                        fullWidth
                                        variant="standard"
                                        label="Detected Malware Max Age"
                                        name="detected_malware_max_age"
                                        value={config.data.retention.detected_malware_max_age}
                                        onChange={handleUpdateRetention}
                                        endAdornment={<InputAdornment position="end">days</InputAdornment>}
                                        disabled={readonly}
                                    /></Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Paper>
            </Grid>
            {/*sandbox*/}
            <Grid item xs={12} style={{ display: mode === 'config' ? 'block' : 'none' }}>
                <Paper elevation={3}>
                    <Box p={2}>
                        <Typography variant="h6">Sandbox</Typography>
                        <Box mt={2}>
                            <Grid container item xs={12} spacing={2}>
                                <Grid item xs={2}>Type</Grid>
                                <Grid item xs={3}>URL </Grid>
                                <Grid item xs={3}>API Key</Grid>
                                <Grid item xs={2}>Report Timeout (30-1800 Seconds)</Grid>
                                <Grid item xs={2}></Grid>
                            </Grid>
                            {config.data.sandbox.configs.map((v, i) => {
                                return (
                                    <SandboxURL
                                        disabled={readonly}
                                        key={'sandbox-url-' + i}
                                        index={i}
                                        sandbox={v}
                                        handleVerify={handleVerifySandboxURL}
                                        handleUpdate={(e) => { handleUpdateSandbox(e, i, v) }}
                                        handleDelete={handleDeleteSandboxURL}
                                        handleValidation={handleValidation}
                                        handleRemoval={handleRemoveValidation}
                                    />
                                );
                            })}
                        </Box>
                        {!readonly && <Box display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="add url"
                                onClick={handleAddSandboxURL}
                            >
                                <AddIcon />
                                URL
                            </Button>
                        </Box>}
                    </Box>
                </Paper>
            </Grid>
            {/*snmp*/}
            <Grid item xs={12} style={{ display: mode === 'config' ? 'block' : 'none' }}>
                <Paper elevation={3}>
                    <Box p={2}>
                        <Typography variant="h6">SNMP</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={5}>
                                <Box>
                                    <TextField
                                        disabled={readonly}
                                        aria-label="system location"
                                        variant="standard"
                                        label="System Location"
                                        fullWidth
                                        value={config.data.snmp.syslocation}
                                        onChange={handleLocation}
                                    />
                                </Box>
                                <Box mt={3}>
                                    <TextField
                                        disabled={readonly}
                                        aria-label="system contact"
                                        variant="standard"
                                        label="System Contact"
                                        fullWidth
                                        value={config.data.snmp.syscontact}
                                        onChange={handleContact}
                                    />
                                </Box>
                                <Box mt={3}>
                                    <TextField
                                        disabled={readonly}
                                        aria-label="read only community string"
                                        variant="standard"
                                        label="Read-Only Community String"
                                        helperText="Comma separated community strings"
                                        fullWidth
                                        value={config.data.snmp.communities || ''}
                                        onChange={handleCommunityStrings}
                                    />
                                </Box>
                                <Box mt={3}>
                                    <Box display="inline-block" mr={2}>
                                        <Button aria-label="Download BRICATA-MIB" download onClick={() => { handleDownloadMIB('BRICATA-MIB') }}>BRICATA-MIB</Button>
                                    </Box>
                                    <Box display="inline-block">
                                        <Button aria-label="Download BRICATA-IF-MIB" download onClick={() => { handleDownloadMIB('BRICATA-IF-MIB') }}>BRICATA-IF-MIB</Button>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={7}>
                                <Box className={classes.fullWidth} height={300}>
                                    {snmpTable && <DataGridPro
                                        components={{
                                            Header: readonly ? undefined : () => {
                                                return (
                                                    <Box display="flex" width="100%" justifyContent="flex-end">
                                                        <IconButton
                                                            variant="contained"
                                                            color="primary"
                                                            aria-label="edit user"
                                                            onClick={handleEditUser}
                                                            disabled={!selectedUser}
                                                            size="large">
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            variant="contained"
                                                            color="secondary"
                                                            aria-label="delete user"
                                                            onClick={handleOpenDeleteUser}
                                                            disabled={!selectedUser}
                                                            size="large">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                );
                                            },
                                        }}
                                        rows={config.data.snmp.users}
                                        columns={[
                                            // {sortable: true, flex: 1, type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Username', field: 'username', renderCell: tableRenderers['username']},
                                            { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Username', field: 'username', renderCell: d => { return <div>{d.row.username}</div> } },
                                            { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Security Level', field: 'seclevel', renderCell: d => { return <div>{d.row.seclevel}</div> } },
                                            { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Auth Protocol', field: 'authtype', renderCell: d => { return <div>{d.row.authtype}</div> } },
                                            { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Privacy Protocol', field: 'privtype', renderCell: d => { return <div>{d.row.privtype}</div> } },
                                        ]}
                                        disableMultipleSelection
                                        onRowClick={handleSelectUser}
                                    />}
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box my={3} display={newUser ? 'none' : 'block'}>
                                    {!readonly && <Button className={classes.buttonRight} variant="contained" color="primary" aria-label="add read-only user" onClick={() => { setNewUser(!newUser) }}>Read-Only User +</Button>}
                                </Box>
                                <Paper elevation={3}>
                                    <Box mt={3} p={5} display={newUser ? 'block' : 'none'}>
                                        <Grid container item xs={12} spacing={5}>
                                            <Grid item xs={6}>
                                                <Box>
                                                    {newUser && <Validator
                                                        unique={config.data.snmp.users.reduce((a, c) => {
                                                            a.push(c.username);
                                                            return a;
                                                        }, [])}
                                                        valueType="snmpName"
                                                        errorText="SNMP username is invalid"
                                                        aria-label="username"
                                                        label="Username"
                                                        fullWidth
                                                        value={newUserData.username}
                                                        onChange={handleUsername}
                                                        onRemoval={handleRemoveValidation}
                                                    />}
                                                </Box>
                                                <Box mt={3}>
                                                    <FormControl fullWidth>
                                                        <InputLabel id="snmp-auth-protocol-label">Auth Protocol</InputLabel>
                                                        <Select
                                                            aria-label="auth protocol"
                                                            labelId="snmp-auth-protocol-label"
                                                            label="Auth Protocol"
                                                            value={newUserData.authtype}
                                                            onChange={handleAuthType}
                                                        >
                                                            <MenuItem value="SHA">SHA</MenuItem>
                                                            <MenuItem value="MD5">MD5</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                                <Box mt={3}>
                                                    <FormControl fullWidth>
                                                        <InputLabel id="snmp-privacy-protocol-label">Privacy Protocol</InputLabel>
                                                        <Select
                                                            aria-label="privacy protocol"
                                                            labelId="snmp-privacy-protocol-label"
                                                            label="Privacy Protocol"
                                                            value={newUserData.privtype}
                                                            onChange={handlePrivType}
                                                        >
                                                            <MenuItem value="AES">AES</MenuItem>
                                                            <MenuItem value="DES">DES</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box>
                                                    <FormControl fullWidth>
                                                        <InputLabel id="snmp-security-level-label">Security Level</InputLabel>
                                                        <Select
                                                            aria-label="security level"
                                                            labelId="snmp-security-level-label"
                                                            label="Security Level"
                                                            value={newUserData.seclevel}
                                                            onChange={handleSeclevel}
                                                        >
                                                            <MenuItem value="authPriv">authPriv</MenuItem>
                                                            <MenuItem value="authNoPriv">authNoPriv</MenuItem>
                                                            <MenuItem value="noAuthNoPriv">noAuthNoPriv</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                                <Box mt={3}>
                                                    {newUser && <Validator
                                                        valueType="length"
                                                        errorText="Must be a minimum of 8 characters"
                                                        inputProps={{ min: 8 }}
                                                        label="Auth Password Phrase"
                                                        aria-label="auth password phrase"
                                                        type="password"
                                                        fullWidth
                                                        value={newUserData.authpass}
                                                        onChange={handleAuthPass}
                                                        onRemoval={handleRemoveValidation}
                                                    />}
                                                </Box>
                                                <Box mt={3}>
                                                    {newUser && <Validator
                                                        valueType="length"
                                                        errorText="Must be a minimum of 8 characters"
                                                        inputProps={{ min: 8 }}
                                                        label="Privacy Password Phrase"
                                                        aria-label="privacy password phrase"
                                                        type="password"
                                                        fullWidth
                                                        value={newUserData.privpass}
                                                        onChange={handlePrivPass}
                                                        onRemoval={handleRemoveValidation}
                                                    />}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <div className={classes.clearfix}>
                                            <Button
                                                className={classes.userButtons}
                                                variant="contained"
                                                color="primary"
                                                aria-label="add"
                                                onClick={handleAddNewUser}
                                                disabled={!checkValidity(configForm.get())}
                                            >
                                                {editUser ? 'Update' : 'Add'}
                                            </Button>
                                            <Button className={classes.userButtons} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancelNewUser}>Cancel</Button>
                                        </div>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                        <ConfirmationDialog
                            onClose={handleCloseDeleteUser}
                            onOK={handleDeleteUser}
                            open={deleteUserDialog}
                            message={`Are you sure you want to remove the user ${selectedUser && selectedUser.username}?`}
                            title="Delete User"
                            cancelLabel="Cancel"
                            okLabel="Yes"
                        />
                    </Box>
                </Paper>
            </Grid>
            {/*dpid*/}
            <Grid item xs={12} style={{ display: mode === 'config' ? 'block' : 'none' }}>
                <Paper elevation={3}>
                    <Box p={2}>
                        <Typography variant="h6">DPI for service classification</Typography>
                        <FormHelperText variant="subtitle2">
                            Leverages the nDPI library on the sensor to provide additional service classification and protocol details in the DPID data source. These records are associated with relevant Alert and Metadata records.
                        </FormHelperText >
                        <Grid container spacing={2}>
                            <Grid item xs={5}>
                                <Box>
                                    <FormControlLabel
                                        disabled={readonly}
                                        label="Enable"
                                        labelPlacement="end"
                                        checked={config.data.dpid.enabled}
                                        control={<Checkbox onClick={handleEnableDPID} />}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default SystemConfig;
